import { BaseService } from '../abstractions/base-service';
import { DnsConfigModel } from 'src/app/shared/models/dns-config.model';
import { catchError, from, map, Observable, of, take } from 'rxjs';
import { DEFAULT_THEME_COLORS, DNS_CONFIG_COLLECTION, OP_EQUALS } from 'src/app/app.constants';
import { FilterModel } from 'src/app/shared/models/filter.model';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
    AngularFirestore,
    DocumentReference,
} from '@angular/fire/compat/firestore';
import { UserService } from './user.service';
import { FireLoggingService } from './fire-logging.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class DnsConfigService {
    constructor(
        private service: BaseService<DnsConfigModel>,
        private firestore: AngularFirestore,
        private userService: UserService,
        private fireLogging: FireLoggingService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {}

    public getDnsConfigByCompanyId(
        companyId: string
    ): Observable<DnsConfigModel> {
        const filter = new FilterModel();
        filter.clauses = [
            {
                fieldPath: 'companyId',
                opStr: OP_EQUALS,
                value: companyId,
            },
        ];
        return this.service.search(DNS_CONFIG_COLLECTION, filter).pipe(
            take(1),
            map((res) => res[0])
        );
    }

    public getDnsConfigByRef(
        reference: DocumentReference
    ): Observable<DnsConfigModel> {
        return from(reference.get()).pipe(
            take(1),
            map((doc) => new DnsConfigModel({ id: doc.id, ...doc.data() }))
        );
    }

    handleThemes() {
        let observable = of({});

        if (isPlatformBrowser(this.platformId)) {
            const [companyDomain, finsteinDomain] =
                window.location.hostname.split('.');

            // Verification required for production dns,
            // avoids having to consult the topic for "portal" before the finstein domain
            //
            // Example:
            //
            // if the hostname is "portal.finstein.de"
            // the companyDomain value is portal
            // and the finsteinDomain is finstein
            //
            // Alternatively if the hostname is "exemple.portal.finstein.de"
            // the companyDomain is example and the finsteinDomain changes to portal
            if (companyDomain && finsteinDomain && finsteinDomain !== 'finstein') {
                observable = this.getCompanyTheme(companyDomain);
            } else {
                this.removeTheme();
            }
        }

        return observable;
    }

    getCompanyTheme(subdomain: string): Observable<any> {
        return this.firestore
            .collection(DNS_CONFIG_COLLECTION, (ref) => {
                return ref.where('subdomain', OP_EQUALS, subdomain);
            })
            .snapshotChanges()
            .pipe(
                map((actions) =>
                    actions.map((action) => {
                        const data: any = action.payload.doc.data();
                        data.id = action.payload.doc.id;
                        return data;
                    })
                ),
                map((data: any) => {
                    const theme = {
                        ...data[0]?.theme?.baseColors,
                        ...data[0]?.theme?.assetsColors,
                        ...data[0]?.theme?.customBackgroundColor,
                        ...data[0]?.theme?.componentsColors,
                        ...data[0]?.theme?.tableColors,
                        ...data[0]?.theme?.sliderColors,
                        ...data[0]?.theme?.portalColors,
                    };

                    if (
                        this.userService.loggedUser?.companyId &&
                        this.userService.loggedUser.companyId !==
                            data[0]?.companyId
                    ) {
                        this.removeTheme();
                    }

                    this.applyTheme(theme);
                    return {
                        copContentLogo: data[0]?.copContentLogo,
                        loginCompanyLogo: data[0]?.loginCompanyLogo,
                    };
                }),
                catchError((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`[getCompanyThemes(dns-config.service.ts)] An error
                    occurred while getting item from collection ${DNS_CONFIG_COLLECTION}, details: ${
                        message || JSON.stringify(error)
                    }`);
                    return of({});
                })
            );
    }

    applyTheme(theme: any) {
        Object.keys(theme).forEach((variable) => {
            if (variable === 'firstBackgroundColor') {
                this.setCssProperty('portalFirstBackgroundColor', theme[variable]);
            }

            if (variable === 'secondBackgroundColor') {
                this.setCssProperty('portalSecondBackgroundColor', theme[variable]);
            }

            if (variable === 'highlightTextColor') {
                this.setCssProperty('portalSideMenuHighlightColor', theme[variable]);
            }

            this.setCssProperty(variable, theme[variable]);
        });
    }

    setCssProperty(variable: string, value: string) {
        document.documentElement.style.setProperty(`--${variable}`, value);
    }

    removeTheme() {
        Object.keys(DEFAULT_THEME_COLORS).forEach((variable) => {
            document.documentElement.style.removeProperty(`--${variable}`);
        });
    }
}
